import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { 
  MessageCircle, 
  ChevronRight,
  ChevronDown,
  Menu,
  X,
  Instagram,
  ArrowLeft,
  Phone,
  Mail,
  MapPin,
  Clock,
  Users,
  Trophy,
  Target,
  Zap,
  Brain,
  Heart,
  Shield,
  Award,
  Calendar,
  Download,
  FileText,
  Check,
  Star
} from "lucide-react";
import { Button } from "@/components/ui/button";
import AkademieRegistrationForm from "@/components/AkademieRegistrationForm";
import RegistrationSuccess from "@/components/RegistrationSuccess";
import SEO from "@/components/SEO";

const WHATSAPP_NUMBER = "491741944996";

const getWhatsAppLink = (message) => {
  const encodedMessage = encodeURIComponent(message);
  return `https://wa.me/${WHATSAPP_NUMBER}?text=${encodedMessage}`;
};

// TikTok Icon
const TikTokIcon = ({ size = 20, className = "" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" className={className}>
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
  </svg>
);

// Navigation Component
const AkademieNavigation = ({ onOpenRegistration }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 bg-[#0A0A0A]/95 backdrop-blur-md border-b border-white/5 ${isScrolled ? "py-3" : "py-4"} transition-all duration-300`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/" className="flex items-center gap-3">
              <img 
                src="https://customer-assets-m6fa6gv7.emergentagent.net/job_coach-reserve-2/artifacts/2ayyxywl_fu%C3%9Fballakademie_logo.png" 
                alt="Coach Yasin - Aktas Fussballakademie"
                className="h-12 w-12 object-contain"
              />
              <div className="hidden sm:block">
                <p className="font-['Bebas_Neue'] text-lg text-white leading-tight">COACH YASIN</p>
                <p className="text-[10px] text-[#bea777] tracking-[0.2em]">AKTAS FUSSBALLAKADEMIE</p>
              </div>
            </Link>
          </div>

          <div className="hidden lg:flex items-center gap-6">
            <a href="#training" className="text-[#A1A1AA] hover:text-white text-sm transition-colors">Training</a>
            <a href="#preise" className="text-[#A1A1AA] hover:text-white text-sm transition-colors">Preise</a>
            <Link to="/" className="text-[#A1A1AA] hover:text-white text-sm transition-colors flex items-center gap-1">
              <ArrowLeft className="w-4 h-4" />
              Hauptseite
            </Link>
            <Link to="/bewerbung">
              <Button
                className="bg-[#bea777] hover:bg-[#d4c9a0] text-[#0A0A0A] font-semibold px-6 py-5 rounded-none"
              >
                JETZT BEWERBEN
              </Button>
            </Link>
          </div>

          <button className="lg:hidden text-white p-2" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}>
            {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {isMobileMenuOpen && (
          <div className="lg:hidden mt-4 pb-4 border-t border-white/10 pt-4 space-y-3">
            <a href="#training" className="block text-[#A1A1AA] hover:text-white py-2" onClick={() => setIsMobileMenuOpen(false)}>Training</a>
            <a href="#preise" className="block text-[#A1A1AA] hover:text-white py-2" onClick={() => setIsMobileMenuOpen(false)}>Preise</a>
            <Link to="/" className="block text-[#bea777] hover:text-white py-2" onClick={() => setIsMobileMenuOpen(false)}>← Hauptseite</Link>
            <Link to="/bewerbung" onClick={() => setIsMobileMenuOpen(false)}>
              <Button
                className="w-full bg-[#bea777] hover:bg-[#d4c9a0] text-[#0A0A0A] font-semibold py-4 rounded-none mt-4"
              >
                JETZT BEWERBEN
              </Button>
            </Link>
          </div>
        )}
      </div>
    </nav>
  );
};

// Hero Section
const HeroSection = ({ onOpenRegistration }) => {
  const stats = [
    { label: "DFB", value: "B-Lizenz" },
    { label: "10+", value: "Jahre Erfahrung" },
    { label: "10–12", value: "Spieler / Gruppe" },
    { label: "U8–U13", value: "Förderjahrgänge" }
  ];

  return (
    <section className="relative min-h-screen flex items-center justify-center pt-20 pb-16 overflow-hidden">
      <div 
        className="absolute inset-0 bg-cover bg-center"
        style={{ backgroundImage: `url('https://images.unsplash.com/photo-1431324155629-1a6deb1dec8d?w=1920&q=80')` }}
      />
      <div className="absolute inset-0 bg-gradient-to-b from-[#0A0A0A]/80 via-[#0A0A0A]/60 to-[#0A0A0A]" />
      
      <div className="relative z-10 max-w-6xl mx-auto px-4 text-center">
        <div className="inline-flex items-center gap-2 bg-[#bea777]/10 border border-[#bea777]/30 px-4 py-2 mb-8">
          <span className="w-2 h-2 bg-[#bea777] rounded-full animate-pulse" />
          <span className="text-[#bea777] text-sm font-medium">Aufnahme 2026 · Sichtung läuft</span>
        </div>

        <h1 className="font-['Bebas_Neue'] text-5xl sm:text-6xl md:text-7xl lg:text-8xl text-white mb-6 leading-[0.9]">
          WIR FÖRDERN SPIELER,<br />
          <span className="text-[#bea777]">DIE MEHR WOLLEN.</span>
        </h1>

        <p className="text-xl md:text-2xl text-white/80 max-w-3xl mx-auto mb-10">
          Leistungsorientierte Fußballförderung für ambitionierte Jahrgänge U8 bis U13. 
          Kleine Gruppen. Individuelle Entwicklung. Professionelle Trainingsumgebung.
        </p>

        <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
          <Link to="/bewerbung">
            <Button
              className="bg-[#bea777] hover:bg-[#d4c9a0] text-[#0A0A0A] font-bold px-10 py-7 rounded-none text-lg"
            >
              Jetzt bewerben
              <ChevronRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
          <Button
            asChild
            variant="outline"
            className="border-2 border-white/30 text-white hover:bg-white/10 px-10 py-7 rounded-none text-lg"
          >
            <a href="#preise">Mitgliedschaft ansehen</a>
          </Button>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-3xl mx-auto mt-16">
          {stats.map((stat) => (
            <div key={stat.label} className="bg-[#1A1A1A]/80 backdrop-blur border border-white/10 p-4">
              <p className="font-['Bebas_Neue'] text-2xl text-[#bea777]">{stat.label}</p>
              <p className="text-white/70 text-sm">{stat.value}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Marquee Banner
const MarqueeBanner = () => {
  const items = ["Disziplin", "Respekt", "Pünktlichkeit", "Einsatzbereitschaft", "Teamfähigkeit", "Lernbereitschaft", "Professionalität"];
  
  return (
    <div className="bg-[#bea777] py-3 overflow-hidden">
      <div className="animate-marquee whitespace-nowrap flex">
        {[...items, ...items, ...items].map((item, i) => (
          <span key={i} className="text-[#0A0A0A] font-semibold mx-8 text-sm">
            {item} <span className="text-[#0A0A0A]/50">✦</span>
          </span>
        ))}
      </div>
    </div>
  );
};

// Philosophie Section
const PhilosophieSection = () => {
  const jahrgaenge = [
    { label: "Jahrgang", title: "U8 / U9", years: "2019 / 2018", desc: "Grundlagentraining mit Fokus auf Koordination, erste Technik & Spielfreude." },
    { label: "Jahrgang", title: "U10 / U11", years: "2017 / 2016", desc: "Aufbautraining mit komplexer Technik, Spielintelligenz & Wettkampfdenken." },
    { label: "Jahrgang", title: "U12 / U13", years: "2016 / 2015", desc: "Leistungstraining, taktische Tiefe, Athletik & gezielte Profilbildung." }
  ];

  const entwicklungsfelder = [
    { icon: Target, label: "Technik" },
    { icon: Brain, label: "Spielintelligenz" },
    { icon: Zap, label: "Handlungs-schnelligkeit" },
    { icon: Trophy, label: "Athletik" },
    { icon: Heart, label: "Mentalität" },
    { icon: Users, label: "Persönlichkeit" },
    { icon: Shield, label: "Wettbewerbs-fähigkeit" }
  ];

  return (
    <section id="philosophie" className="py-20 lg:py-32 bg-[#0A0A0A]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <p className="text-[#bea777] text-sm uppercase tracking-wider mb-4">Philosophie</p>
          <h2 className="font-['Bebas_Neue'] text-4xl md:text-6xl text-white mb-6">
            EINE AKADEMIE. <span className="text-[#bea777]">KEIN VEREIN.</span>
          </h2>
          <p className="text-[#A1A1AA] text-lg max-w-3xl mx-auto">
            Die Aktas Fussballakademie ist eine professionelle Förderakademie – kein Verein, kein Freizeitangebot. 
            Wir entwickeln Spieler, die mehr wollen, und verstehen uns als Ergänzung zum Heimatverein, der jederzeit Vorrang hat.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16">
          {jahrgaenge.map((jg) => (
            <div key={jg.title} className="bg-[#1A1A1A] border border-white/5 p-8 hover:border-[#bea777]/30 transition-all">
              <p className="text-[#bea777] text-xs uppercase tracking-wider mb-2">{jg.label}</p>
              <h3 className="font-['Bebas_Neue'] text-3xl text-white mb-1">{jg.title}</h3>
              <p className="text-[#71717A] text-sm mb-4">{jg.years}</p>
              <p className="text-[#A1A1AA]">{jg.desc}</p>
            </div>
          ))}
        </div>

        <div className="bg-[#121212] border border-white/5 p-8 md:p-12">
          <h3 className="font-['Bebas_Neue'] text-2xl text-white mb-2">Entwicklungsfelder</h3>
          <p className="text-[#71717A] mb-8">Sieben Säulen, an denen jeder Spieler kontinuierlich arbeitet.</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-7 gap-3 sm:gap-4">
            {entwicklungsfelder.map((feld) => (
              <div key={feld.label} className="bg-[#1A1A1A] border border-white/5 p-3 sm:p-4 text-center hover:border-[#bea777]/30 transition-all group min-h-[100px] flex flex-col items-center justify-center">
                <feld.icon className="w-6 h-6 sm:w-8 sm:h-8 text-[#bea777] mb-2 sm:mb-3 group-hover:scale-110 transition-transform flex-shrink-0" />
                <p className="text-white text-xs sm:text-sm leading-tight break-words hyphens-auto text-center w-full">{feld.label}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

// Training Section
const TrainingSection = () => {
  const features = [
    "4 Trainingseinheiten pro Monat",
    "Jeden Sonntag · 75 Minuten",
    "Max. 10–12 Feldspieler + 1–2 Torhüter",
    "Individuelle Förderung in Kleingruppen",
    "Freundschaftsspiele & Leistungsvergleiche",
    "Sichtungstage, Turniere & Auswahlteams"
  ];

  const torwartFeatures = [
    "1 Torwarttraining pro Monat",
    "Spezifisches Techniktraining",
    "Stellungsspiel & Fangtechnik",
    "Spieleröffnung",
    "Reaktionsschulung"
  ];

  return (
    <section id="training" className="py-20 lg:py-32 bg-[#121212]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-20">
          <div>
            <p className="text-[#bea777] text-sm uppercase tracking-wider mb-4">Trainingskonzept</p>
            <h2 className="font-['Bebas_Neue'] text-4xl md:text-5xl text-white mb-6">
              KLEINE GRUPPEN. <span className="text-[#bea777]">MAXIMALE WIRKUNG.</span>
            </h2>
            <p className="text-[#A1A1AA] text-lg mb-8">
              Gruppen werden bewusst klein gehalten – damit jeder Spieler die individuelle Aufmerksamkeit erhält, 
              die echte Entwicklung möglich macht.
            </p>
            <ul className="space-y-3">
              {features.map((f) => (
                <li key={f} className="flex items-center gap-3 text-white">
                  <Check className="w-5 h-5 text-[#bea777]" />
                  {f}
                </li>
              ))}
            </ul>
          </div>
          <div className="relative">
            <img 
              src="https://static.prod-images.emergentagent.com/jobs/a6eef84e-6199-4377-8945-c31d167f9332/images/5b599ceece748c5139fa2104ae02f3c356e79c6434b718ee3b6a939348e6d6e9.png" 
              alt="Gruppentraining"
              className="w-full rounded-lg"
            />
          </div>
        </div>

        {/* Torwartförderung */}
        <div className="bg-[#1A1A1A] border border-white/5 p-8 md:p-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-[#bea777] text-sm uppercase tracking-wider mb-4">Torwartförderung</p>
              <h3 className="font-['Bebas_Neue'] text-3xl md:text-4xl text-white mb-6">
                SPEZIALTRAINING <span className="text-[#bea777]">FÜR TORHÜTER</span>
              </h3>
              <p className="text-[#A1A1AA] mb-6">
                Jeder Torhüter erhält zusätzlich zur Akademie-Einheit ein eigenständiges, 
                vollständig auf seine Position abgestimmtes Training.
              </p>
              <ul className="space-y-2">
                {torwartFeatures.map((f) => (
                  <li key={f} className="flex items-center gap-3 text-white">
                    <Check className="w-4 h-4 text-[#bea777]" />
                    {f}
                  </li>
                ))}
              </ul>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div className="bg-[#0A0A0A] border border-white/10 p-6 text-center">
                <p className="font-['Bebas_Neue'] text-3xl text-[#bea777]">1X</p>
                <p className="text-white/70 text-sm">Pro Monat</p>
              </div>
              <div className="bg-[#0A0A0A] border border-white/10 p-6 text-center">
                <p className="font-['Bebas_Neue'] text-3xl text-[#bea777]">75&apos;</p>
                <p className="text-white/70 text-sm">Pro Einheit</p>
              </div>
              <div className="bg-[#0A0A0A] border border-white/10 p-6 text-center">
                <p className="font-['Bebas_Neue'] text-2xl text-[#bea777]">Sonntags</p>
                <p className="text-white/70 text-sm">Fester Termin</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Ausrüstung Section
const AusruestungSection = () => {
  const items = [
    { num: "01", title: "Akademie Trainingsshirt", desc: "Premium Performance Fabric" },
    { num: "02", title: "Trinkflasche", desc: "Akademie Branding" },
    { num: "03", title: "Sportbeutel", desc: "Mit Logo & Schriftzug" }
  ];

  return (
    <section className="py-20 lg:py-32 bg-[#0A0A0A]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <img 
              src="https://static.prod-images.emergentagent.com/jobs/a6eef84e-6199-4377-8945-c31d167f9332/images/9914c176059308bde08304dc05021931815303c0dac3c7f237a7fb6cd4e5f10c.png" 
              alt="Akademie-Paket"
              className="w-full rounded-lg"
            />
          </div>
          <div>
            <p className="text-[#bea777] text-sm uppercase tracking-wider mb-4">Im Beitrag inklusive</p>
            <h2 className="font-['Bebas_Neue'] text-4xl md:text-5xl text-white mb-6">
              DAS <span className="text-[#bea777]">AKADEMIE-PAKET</span>
            </h2>
            <p className="text-[#A1A1AA] text-lg mb-8">
              Jeder Spieler erhält bei der Anmeldung das vollständige Akademie-Ausstattungspaket. 
              Bereits enthalten im Mitgliedsbeitrag.
            </p>
            <div className="space-y-4">
              {items.map((item) => (
                <div key={item.num} className="flex items-center gap-4 bg-[#1A1A1A] border border-white/5 p-4">
                  <span className="font-['Bebas_Neue'] text-2xl text-[#bea777]">{item.num}</span>
                  <div>
                    <p className="text-white font-medium">{item.title}</p>
                    <p className="text-[#71717A] text-sm">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Camps Teaser Section (Links to dedicated page)
const CampsSection = () => {
  return (
    <section id="camps" className="py-20 lg:py-32 bg-[#121212]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <p className="text-[#bea777] text-sm uppercase tracking-wider mb-4">Sommer Camps 2026</p>
            <h2 className="font-['Bebas_Neue'] text-4xl md:text-5xl text-white mb-6">
              VIER TAGE. <span className="text-[#bea777]">VOLLE INTENSITÄT.</span>
            </h2>
            <p className="text-[#A1A1AA] text-lg mb-8">
              Professionelles Camp-Training in den Sommerferien für Kinder von 6 bis 13 Jahren.
            </p>

            <div className="grid grid-cols-2 gap-4 mb-8">
              <div className="bg-[#1A1A1A] border border-white/10 p-4 text-center">
                <p className="font-['Bebas_Neue'] text-2xl text-[#bea777]">180€</p>
                <p className="text-white/70 text-xs">Pro Camp</p>
              </div>
              <div className="bg-[#1A1A1A] border border-white/10 p-4 text-center">
                <p className="font-['Bebas_Neue'] text-2xl text-[#bea777]">10–14</p>
                <p className="text-white/70 text-xs">Uhr · Intensiv</p>
              </div>
              <div className="bg-[#1A1A1A] border border-white/10 p-4 text-center">
                <p className="font-['Bebas_Neue'] text-2xl text-[#bea777]">6–13</p>
                <p className="text-white/70 text-xs">Jahre</p>
              </div>
              <div className="bg-[#1A1A1A] border border-white/10 p-4 text-center">
                <p className="font-['Bebas_Neue'] text-2xl text-[#bea777]">10%</p>
                <p className="text-white/70 text-xs">Frühbucher</p>
              </div>
            </div>

            <Button
              asChild
              className="bg-[#bea777] hover:bg-[#d4c9a0] text-[#0A0A0A] font-bold px-10 py-7 rounded-none text-lg"
            >
              <Link to="/camps">
                Alle Camps ansehen
                <ChevronRight className="w-5 h-5 ml-2" />
              </Link>
            </Button>
          </div>
          <div>
            <img 
              src="https://static.prod-images.emergentagent.com/jobs/a6eef84e-6199-4377-8945-c31d167f9332/images/ef7f66392f4adf8cb629d4d06ea3cefa406bd4c26be68651997132ff4cf14629.png" 
              alt="Sommer Camp"
              className="w-full rounded-lg"
            />
          </div>
        </div>
      </div>
    </section>
  );
};

// Preise Section  
const PreiseSection = ({ onOpenRegistration }) => {
  const plans = [
    {
      title: "Halbjahresvertrag",
      duration: "6 Monate Laufzeit",
      price: "79",
      total: "474€",
      oldTotal: null,
      savings: null,
      features: ["6 Monate Mitgliedschaft", "Alle Trainings inklusive", "Akademie-Ausstattung", "15 % Rabatt auf Camps"],
      popular: false
    },
    {
      title: "Jahresvertrag",
      duration: "12 Monate Laufzeit",
      price: "64,83",
      total: "778€",
      oldTotal: null,
      savings: "50€",
      features: ["12 Monate Mitgliedschaft", "Bester Preis pro Monat", "Akademie-Ausstattung", "Prioritat bei Sichtung", "15 % Rabatt auf Camps"],
      popular: true
    },
    {
      title: "Monatsvertrag",
      duration: "Monatlich kundbar",
      price: "89",
      total: null,
      features: ["Maximale Flexibilitat", "Monatlich kundbar", "Alle Trainings inklusive", "Akademie-Ausstattung"],
      popular: false
    }
  ];

  return (
    <section id="preise" className="py-20 lg:py-32 bg-[#0A0A0A]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <p className="text-[#bea777] text-sm uppercase tracking-wider mb-4">Mitgliedschaften</p>
          <h2 className="font-['Bebas_Neue'] text-4xl md:text-6xl text-white mb-6">
            WERDE TEIL <span className="text-[#bea777]">DER AKADEMIE</span>
          </h2>
          <p className="text-[#A1A1AA] text-lg max-w-2xl mx-auto">
            Drei Wege in die Förderung. Die ersten 10 Anmeldungen pro Vertrag sichern sich einen exklusiven Frühbucher-Bonus.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {plans.map((plan) => (
            <div 
              key={plan.title} 
              className={`relative bg-[#1A1A1A] border ${plan.popular ? 'border-[#bea777]' : 'border-white/5'} p-8`}
            >
              {plan.popular && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#bea777] text-[#0A0A0A] text-xs font-bold px-4 py-1">
                  Empfohlen
                </div>
              )}
              <div className="text-center mb-6">
                <h3 className="font-['Bebas_Neue'] text-2xl text-white mb-1">{plan.title}</h3>
                <p className="text-[#71717A] text-sm">{plan.duration}</p>
              </div>
              <div className="text-center mb-6">
                <p className="font-['Bebas_Neue'] text-5xl text-white">{plan.price}€</p>
                <p className="text-[#71717A]">/ Monat</p>
              </div>
              {plan.total && (
                <div className="text-center mb-6 bg-[#0A0A0A] p-3">
                  <p className="text-[#71717A] text-sm">
                    {plan.duration.includes("6") ? "Halbjahressumme" : "Jahressumme"}
                  </p>
                  <p className="text-white">
                    <span className="font-bold">{plan.total}</span>
                    <span className="text-[#71717A] line-through ml-2">{plan.oldTotal}</span>
                  </p>
                  <p className="text-[#bea777] text-sm">Du sparst {plan.savings}</p>
                </div>
              )}
              {plan.total && (
                <p className="text-[#bea777] text-xs text-center mb-4">
                  Nur noch 10 / 10 Bonus-Plätze
                </p>
              )}
              <ul className="space-y-2 mb-6">
                {plan.features.map((f) => (
                  <li key={f} className="flex items-center gap-2 text-white/80 text-sm">
                    <Check className="w-4 h-4 text-[#bea777]" />
                    {f}
                  </li>
                ))}
              </ul>
              <Link to="/bewerbung" className="w-full">
                <Button
                  className={`w-full rounded-none py-6 ${plan.popular ? 'bg-[#bea777] hover:bg-[#d4c9a0] text-[#0A0A0A]' : 'bg-transparent border-2 border-[#bea777] text-white hover:bg-[#bea777] hover:text-[#0A0A0A]'}`}
                >
                  Jetzt bewerben
                </Button>
              </Link>
            </div>
          ))}
        </div>

        {/* Mitgliedervorteile */}
        <div className="mt-16 bg-[#121212] border border-white/5 p-8 md:p-12">
          <h3 className="font-['Bebas_Neue'] text-2xl text-white mb-6">
            Was du als Akademie-<span className="text-[#bea777]">Mitglied erhältst</span>
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              "15 % Rabatt auf alle Camps",
              "Zugang zu Sichtungstagen",
              "Teilnahme an Auswahlteams möglich",
              "Individuelle Förderung",
              "Professionelle Trainingsumgebung",
              "Akademie-Ausstattung inklusive"
            ].map((benefit) => (
              <div key={benefit} className="flex items-center gap-3 text-white/80">
                <Check className="w-5 h-5 text-[#bea777]" />
                {benefit}
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

// Leistungsprinzip Section
const LeistungsprinzipSection = () => {
  const prinzipien = ["Disziplin", "Respekt", "Pünktlichkeit", "Einsatzbereitschaft", "Teamfähigkeit", "Lernbereitschaft", "Professionelles Verhalten"];
  const kriterien = ["Trainingsbeteiligung", "Leistungsstand", "Entwicklungspotenzial", "Teamfähigkeit", "Disziplin", "Zuverlässigkeit", "Verhalten", "Einsatzbereitschaft"];

  return (
    <section className="py-20 lg:py-32 bg-[#121212]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <p className="text-[#bea777] text-sm uppercase tracking-wider mb-4">Leistungsprinzip</p>
          <h2 className="font-['Bebas_Neue'] text-4xl md:text-6xl text-white mb-6">
            WIR ERWARTEN <span className="text-[#bea777]">MEHR.</span>
          </h2>
          <p className="text-[#A1A1AA] text-lg max-w-3xl mx-auto">
            Unsere Akademie ist kein Freizeit- oder Betreuungsangebot. Sie richtet sich an Spieler mit echtem Anspruch – 
            an sich selbst, ihre Entwicklung und ihre Mitspieler.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div className="bg-[#1A1A1A] border border-white/5 p-8">
            <h3 className="font-['Bebas_Neue'] text-2xl md:text-3xl text-white mb-6">Unsere Erwartungen</h3>
            <ol className="space-y-4">
              {prinzipien.map((p, i) => (
                <li key={p} className="flex items-center gap-4 text-white/80 text-base md:text-lg">
                  <span className="w-10 h-10 bg-[#bea777]/20 flex items-center justify-center text-[#bea777] font-bold text-base">
                    {String(i + 1).padStart(2, '0')}
                  </span>
                  {p}
                </li>
              ))}
            </ol>
          </div>

          <div className="space-y-6">
            <div className="bg-[#1A1A1A] border border-white/5 p-8">
              <h3 className="font-['Bebas_Neue'] text-2xl md:text-3xl text-white mb-4">Turniere & Auswahl</h3>
              <p className="text-[#A1A1AA] text-base md:text-lg mb-4">
                Wir nehmen regelmäßig an externen Wettbewerben teil – als Vergleich, als Bühne und als Erfahrung.
              </p>
              <div className="flex flex-wrap gap-2">
                {["Leistungsvergleiche", "Freundschaftsspiele", "Turniere", "Sichtungsturniere"].map((t) => (
                  <span key={t} className="bg-[#0A0A0A] text-white/70 text-base px-4 py-2">{t}</span>
                ))}
              </div>
              <p className="text-[#71717A] text-sm md:text-base mt-4">
                Die Mitgliedschaft bedeutet nicht automatisch eine Teilnahme. Die Nominierung erfolgt ausschließlich durch die sportliche Leitung.
              </p>
            </div>

            <div className="bg-[#1A1A1A] border border-white/5 p-8">
              <h3 className="font-['Bebas_Neue'] text-2xl md:text-3xl text-white mb-4">Auswahlkriterien</h3>
              <p className="text-[#71717A] text-base md:text-lg mb-4">
                Bei der Nominierung berücksichtigen wir vielschichtige Kriterien – ein Anspruch auf eine Nominierung besteht nicht.
              </p>
              <div className="grid grid-cols-2 gap-3">
                {kriterien.map((k) => (
                  <div key={k} className="flex items-center gap-2 text-white/70 text-base md:text-lg">
                    <Check className="w-5 h-5 text-[#bea777]" />
                    {k}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Trainer Section
const TrainerTeamSection = () => {
  const mentors = [
    { initials: "IB", name: "Irfan Buz", role: "Profi-Trainer" },
    { initials: "IP", name: "Ingo Peters", role: "Profi-Trainer" },
    { initials: "NS", name: "Nuri Sahin", role: "Profi-Spieler & Trainer" }
  ];

  return (
    <section id="team" className="py-20 lg:py-32 bg-[#0A0A0A]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <p className="text-[#bea777] text-sm uppercase tracking-wider mb-4">Trainerteam</p>
          <h2 className="font-['Bebas_Neue'] text-4xl md:text-6xl text-white mb-6">
            HÖCHSTE <span className="text-[#bea777]">AUSBILDUNGSQUALITÄT</span>
          </h2>
          <p className="text-[#A1A1AA] text-lg max-w-3xl mx-auto">
            Geführt von qualifizierten und leistungsorientierten Trainern. Im Aufbau befindet sich ein professionelles Trainerteam.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          <div className="bg-[#1A1A1A] border border-white/5 p-8">
            <div className="flex items-start gap-6 mb-8">
              <div className="w-24 h-24 bg-[#bea777]/20 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="font-['Bebas_Neue'] text-3xl text-[#bea777]">YA</span>
              </div>
              <div>
                <p className="text-[#bea777] text-sm uppercase tracking-wider">Akademieleiter</p>
                <h3 className="font-['Bebas_Neue'] text-3xl text-white">Yasin Aktas</h3>
                <div className="flex flex-wrap gap-2 mt-2">
                  <span className="bg-[#0A0A0A] text-[#bea777] text-xs px-2 py-1">DFB B-Lizenz</span>
                  <span className="bg-[#0A0A0A] text-[#bea777] text-xs px-2 py-1">Westfalenliga</span>
                  <span className="bg-[#0A0A0A] text-[#bea777] text-xs px-2 py-1">10+ Jahre Erfahrung</span>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <div className="bg-[#0A0A0A] p-4">
                <p className="text-[#bea777] text-xs uppercase mb-1">Lizenz</p>
                <p className="text-white font-medium">DFB B-Lizenz</p>
                <p className="text-[#71717A] text-sm">Offiziell lizenziert – Garant für strukturiertes, methodisches Training auf hohem Niveau.</p>
              </div>
              <div className="bg-[#0A0A0A] p-4">
                <p className="text-[#bea777] text-xs uppercase mb-1">Spielerische Laufbahn</p>
                <p className="text-white font-medium">Westfalenliga</p>
                <p className="text-[#71717A] text-sm">Eigene aktive Karriere bis in den höchsten Amateurbereich.</p>
              </div>
            </div>
          </div>

          <div>
            <div className="bg-[#1A1A1A] border border-white/5 p-8">
              <h4 className="font-['Bebas_Neue'] text-xl text-white mb-6">Geprägt von Profis</h4>
              <div className="space-y-4">
                {mentors.map((m) => (
                  <div key={m.name} className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-[#bea777]/20 rounded-full flex items-center justify-center">
                      <span className="text-[#bea777] font-bold text-sm">{m.initials}</span>
                    </div>
                    <div>
                      <p className="text-white font-medium">{m.name}</p>
                      <p className="text-[#71717A] text-sm">{m.role}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// CTA Section
const CTASection = ({ onOpenRegistration }) => {
  const downloads = [
    { name: "Akademievertrag (PDF)", url: "https://customer-assets.emergentagent.com/job_web-enhance-48/artifacts/v9w8530a_NextLevel_Akademievertrag.pdf" },
    { name: "Anmeldeformular", url: "https://customer-assets.emergentagent.com/job_web-enhance-48/artifacts/6lzeb0s5_NextLevel_Anmeldeformular.pdf" },
    { name: "SEPA-Lastschriftmandat", url: "https://customer-assets.emergentagent.com/job_web-enhance-48/artifacts/rpdhavq3_NextLevel_SEPA.pdf" },
    { name: "Foto- und Videoeinwilligung", url: "https://customer-assets.emergentagent.com/job_web-enhance-48/artifacts/zf7xjfqy_NextLevel_FotoVideo.pdf" },
    { name: "Datenschutzinformation", url: "https://customer-assets.emergentagent.com/job_web-enhance-48/artifacts/nu9bfl6p_NextLevel_DSGVO.pdf" }
  ];

  return (
    <section id="bewerben" className="py-20 lg:py-32 bg-[#121212] relative overflow-hidden">
      <div 
        className="absolute inset-0 bg-cover bg-center opacity-20"
        style={{ backgroundImage: `url('https://images.unsplash.com/photo-1431324155629-1a6deb1dec8d?w=1920&q=80')` }}
      />
      <div className="absolute inset-0 bg-gradient-to-t from-[#121212] via-[#121212]/80 to-[#121212]" />
      
      <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <p className="text-[#bea777] text-sm uppercase tracking-wider mb-4">Jetzt bewerben</p>
          <h2 className="font-['Bebas_Neue'] text-4xl md:text-6xl text-white mb-6">
            SICHERE DIR <span className="text-[#bea777]">DEINEN PLATZ.</span>
          </h2>
          <p className="text-[#A1A1AA] text-lg max-w-2xl mx-auto mb-8">
            Du möchtest Teil unserer Leistungsakademie werden? Bewirb dich jetzt digital – schnell, einfach und unverbindlich.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
            <Link to="/bewerbung">
              <Button
                className="bg-[#bea777] hover:bg-[#d4c9a0] text-[#0A0A0A] font-bold px-10 py-7 rounded-none text-lg w-full"
              >
                Jetzt bewerben
                <ChevronRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
            <Button
              onClick={() => window.open(getWhatsAppLink("Hallo Yasin, ich habe eine Frage zur Aktas Fussballakademie."), "_blank")}
              variant="outline"
              className="border-2 border-white/30 text-white hover:bg-white/10 px-10 py-7 rounded-none text-lg"
            >
              <MessageCircle className="w-5 h-5 mr-2" />
              Fragen per WhatsApp
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-12">
          <a href="tel:+4901741944996" className="bg-[#1A1A1A] border border-white/5 p-6 text-center hover:border-[#bea777]/30 transition-all">
            <Phone className="w-6 h-6 text-[#bea777] mx-auto mb-2" />
            <p className="text-white/50 text-sm">Telefon</p>
            <p className="text-white">0174 194 4996</p>
          </a>
          <a href={getWhatsAppLink("Hallo Yasin, ich möchte mich für die Aktas Fussballakademie bewerben.")} target="_blank" rel="noopener noreferrer" className="bg-[#1A1A1A] border border-white/5 p-6 text-center hover:border-[#bea777]/30 transition-all">
            <MessageCircle className="w-6 h-6 text-[#bea777] mx-auto mb-2" />
            <p className="text-white/50 text-sm">WhatsApp</p>
            <p className="text-white">0174 194 4996</p>
          </a>
          <a href="mailto:kontakt@personal-fussballtraining.de" className="bg-[#1A1A1A] border border-white/5 p-6 text-center hover:border-[#bea777]/30 transition-all">
            <Mail className="w-6 h-6 text-[#bea777] mx-auto mb-2" />
            <p className="text-white/50 text-sm">E-Mail</p>
            <p className="text-white text-sm">kontakt@personal-fussballtraining.de</p>
          </a>
        </div>

        {/* Downloads Section - temporär ausgeblendet bis neue Downloads erstellt wurden
        <div className="bg-[#1A1A1A] border border-white/5 p-8">
          <h3 className="font-['Bebas_Neue'] text-xl text-white mb-2">Downloads</h3>
          <p className="text-[#71717A] text-sm mb-6">PDF · Direktdownload</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {downloads.map((d) => (
              <a 
                key={d.name}
                href={d.url} 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center gap-3 bg-[#0A0A0A] p-4 hover:bg-[#bea777]/10 transition-colors group"
              >
                <Download className="w-5 h-5 text-[#bea777]" />
                <span className="text-white/80 text-sm group-hover:text-white">{d.name}</span>
              </a>
            ))}
          </div>
        </div>
        */}
      </div>
    </section>
  );
};

// Footer
const AkademieFooter = () => (
  <footer className="py-10 bg-[#0A0A0A] border-t border-white/5">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="flex flex-col md:flex-row items-center justify-between gap-6">
        <div className="flex items-center gap-4">
          <img 
            src="https://customer-assets-m6fa6gv7.emergentagent.net/job_coach-reserve-2/artifacts/2ayyxywl_fu%C3%9Fballakademie_logo.png" 
            alt="Logo"
            className="h-10 w-10 object-contain"
          />
          <div>
            <p className="font-['Bebas_Neue'] text-white">COACH YASIN · AKTAS FUSSBALLAKADEMIE</p>
            <p className="text-[#71717A] text-xs">Yasin Aktas · Meinerzhagen</p>
          </div>
        </div>
        <div className="flex items-center gap-6">
          <a href="https://instagram.com/fussballtraining_aktas" target="_blank" rel="noopener noreferrer" className="text-[#A1A1AA] hover:text-[#bea777]">
            <Instagram className="w-5 h-5" />
          </a>
          <a href="https://tiktok.com/@fussballtraining_aktas" target="_blank" rel="noopener noreferrer" className="text-[#A1A1AA] hover:text-[#bea777]">
            <TikTokIcon size={20} />
          </a>
        </div>
        <div className="text-center md:text-right">
          <Link to="/" className="text-[#A1A1AA] hover:text-white text-sm">Zurück zur Hauptseite</Link>
          <p className="text-[#71717A] text-xs mt-2">© {new Date().getFullYear()} Yasin Aktas. Alle Rechte vorbehalten.</p>
        </div>
      </div>
    </div>
  </footer>
);

// Main Page Component
export default function AkademiePage() {
  const [isLoaded, setIsLoaded] = useState(false);
  const [showRegistrationForm, setShowRegistrationForm] = useState(false);
  const [registrationSuccess, setRegistrationSuccess] = useState(null);

  useEffect(() => {
    setIsLoaded(true);
    window.scrollTo(0, 0);
  }, []);

  const handleOpenRegistration = () => {
    setShowRegistrationForm(true);
  };

  const handleRegistrationSuccess = (data) => {
    setShowRegistrationForm(false);
    setRegistrationSuccess(data);
  };

  const handleCloseSuccess = () => {
    setRegistrationSuccess(null);
  };

  return (
    <main className={`transition-opacity duration-700 ${isLoaded ? 'opacity-100' : 'opacity-0'}`}>
      <SEO 
        title="Fußballakademie | Personal Fußballtraining"
        description="Die Fußballakademie für ambitionierte Spieler: Individuelles Fördertraining, moderne Trainingsmethoden und gezielte Entwicklung deiner fußballerischen Fähigkeiten."
        path="/akademie"
      />
      <AkademieNavigation onOpenRegistration={handleOpenRegistration} />
      <HeroSection onOpenRegistration={handleOpenRegistration} />
      <MarqueeBanner />
      <PhilosophieSection />
      <TrainingSection />
      <AusruestungSection />
      <PreiseSection onOpenRegistration={handleOpenRegistration} />
      <LeistungsprinzipSection />
      <TrainerTeamSection />
      <CTASection onOpenRegistration={handleOpenRegistration} />
      <AkademieFooter />
      
      {/* Registration Form Modal */}
      {showRegistrationForm && (
        <AkademieRegistrationForm
          onClose={() => setShowRegistrationForm(false)}
          onSuccess={handleRegistrationSuccess}
        />
      )}
      
      {/* Registration Success Modal */}
      {registrationSuccess && (
        <RegistrationSuccess
          data={registrationSuccess}
          onClose={handleCloseSuccess}
        />
      )}
    </main>
  );
}
